import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { PlusIcon, PencilIcon, TrashIcon, EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline';
import apiService from '../../services/api';
import { SubscriptionPlan, CreateSubscriptionPlanData } from '../../types';

interface SubscriptionPlansProps {
  isAdmin?: boolean;
}

const SubscriptionPlans: React.FC<SubscriptionPlansProps> = ({ isAdmin = false }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingPlan, setEditingPlan] = useState<SubscriptionPlan | null>(null);
  const [formData, setFormData] = useState<CreateSubscriptionPlanData>({
    name: '',
    description: '',
    price: 0,
    duration: 1,
    durationUnit: 'month',
    features: [],
    maxUsers: 5,
    maxBuses: 10,
    maxTrips: 100
  });

  const queryClient = useQueryClient();

  // Fetch subscription plans
  const { data: plans, isLoading, error } = useQuery({
    queryKey: ['subscriptionPlans', isAdmin],
    queryFn: () => isAdmin ? apiService.getSubscriptionPlansAdmin() : apiService.getSubscriptionPlans()
  });

  // Create subscription plan mutation
  const createMutation = useMutation({
    mutationFn: (data: CreateSubscriptionPlanData) => apiService.createSubscriptionPlan(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['subscriptionPlans'] });
      setIsModalOpen(false);
      resetForm();
    }
  });

  // Update subscription plan mutation
  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<CreateSubscriptionPlanData> }) => 
      apiService.updateSubscriptionPlan(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['subscriptionPlans'] });
      setIsModalOpen(false);
      setEditingPlan(null);
      resetForm();
    }
  });

  // Delete subscription plan mutation
  const deleteMutation = useMutation({
    mutationFn: (id: string) => apiService.deleteSubscriptionPlan(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['subscriptionPlans'] });
    }
  });

  // Toggle subscription plan status mutation
  const toggleMutation = useMutation({
    mutationFn: (id: string) => apiService.toggleSubscriptionPlan(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['subscriptionPlans'] });
    }
  });

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      price: 0,
      duration: 1,
      durationUnit: 'month',
      features: [],
      maxUsers: 5,
      maxBuses: 10,
      maxTrips: 100
    });
  };

  const openCreateModal = () => {
    setEditingPlan(null);
    resetForm();
    setIsModalOpen(true);
  };

  const openEditModal = (plan: SubscriptionPlan) => {
    setEditingPlan(plan);
    setFormData({
      name: plan.name,
      description: plan.description,
      price: plan.price,
      duration: plan.duration,
      durationUnit: plan.durationUnit,
      features: plan.features,
      maxUsers: plan.maxUsers,
      maxBuses: plan.maxBuses,
      maxTrips: plan.maxTrips
    });
    setIsModalOpen(true);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (editingPlan) {
      updateMutation.mutate({ id: editingPlan._id, data: formData });
    } else {
      createMutation.mutate(formData);
    }
  };

  const handleFeatureChange = (index: number, value: string) => {
    const newFeatures = [...(formData.features || [])];
    newFeatures[index] = value;
    setFormData(prev => ({ ...prev, features: newFeatures }));
  };

  const addFeature = () => {
    setFormData(prev => ({ ...prev, features: [...(prev.features || []), ''] }));
  };

  const removeFeature = (index: number) => {
    const newFeatures = (formData.features || []).filter((_, i) => i !== index);
    setFormData(prev => ({ ...prev, features: newFeatures }));
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <p className="text-red-600">خطأ في تحميل خطط الاشتراك</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">خطط الاشتراك</h1>
          <p className="text-gray-600">إدارة خطط الاشتراك المتاحة للبائعين</p>
        </div>
        {isAdmin && (
          <button
            onClick={openCreateModal}
            className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            <PlusIcon className="h-5 w-5 ml-2" />
            إضافة خطة جديدة
          </button>
        )}
      </div>

      {/* Plans Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {plans?.map((plan) => (
          <div
            key={plan._id}
            className={`bg-white rounded-lg shadow-md border-2 ${
              plan.isActive ? 'border-green-200' : 'border-red-200'
            }`}
          >
            {/* Plan Header */}
            <div className="p-6 border-b border-gray-200">
              <div className="flex justify-between items-start mb-4">
                <div>
                  <h3 className="text-xl font-semibold text-gray-900">{plan.name}</h3>
                  <p className="text-gray-600 text-sm mt-1">{plan.description}</p>
                </div>
                {isAdmin && (
                  <div className="flex space-x-2 space-x-reverse">
                    <button
                      onClick={() => toggleMutation.mutate(plan._id)}
                      className={`p-1 rounded ${
                        plan.isActive ? 'text-green-600 hover:text-green-800' : 'text-red-600 hover:text-red-800'
                      }`}
                      title={plan.isActive ? 'إلغاء التفعيل' : 'تفعيل'}
                    >
                      {plan.isActive ? <EyeIcon className="h-4 w-4" /> : <EyeSlashIcon className="h-4 w-4" />}
                    </button>
                    <button
                      onClick={() => openEditModal(plan)}
                      className="p-1 text-blue-600 hover:text-blue-800 rounded"
                      title="تعديل"
                    >
                      <PencilIcon className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => {
                        if (window.confirm('هل أنت متأكد من حذف هذه الخطة؟')) {
                          deleteMutation.mutate(plan._id);
                        }
                      }}
                      className="p-1 text-red-600 hover:text-red-800 rounded"
                      title="حذف"
                    >
                      <TrashIcon className="h-4 w-4" />
                    </button>
                  </div>
                )}
              </div>

              {/* Price */}
              <div className="text-3xl font-bold text-blue-600 mb-2">
                {plan.price} ريال
                <span className="text-sm font-normal text-gray-500">
                  /{plan.durationUnit === 'month' ? 'شهر' : 'سنة'}
                </span>
              </div>

              {/* Duration */}
              <p className="text-gray-600 text-sm">
                المدة: {plan.duration} {plan.durationUnit === 'month' ? 'شهر' : 'سنة'}
              </p>
            </div>

            {/* Plan Limits */}
            <div className="p-6 border-b border-gray-200">
              <div className="grid grid-cols-3 gap-4 text-center">
                <div>
                  <div className="text-lg font-semibold text-gray-900">
                    {plan.maxUsers === -1 ? '∞' : plan.maxUsers}
                  </div>
                  <div className="text-xs text-gray-500">مستخدم</div>
                </div>
                <div>
                  <div className="text-lg font-semibold text-gray-900">
                    {plan.maxBuses === -1 ? '∞' : plan.maxBuses}
                  </div>
                  <div className="text-xs text-gray-500">حافلة</div>
                </div>
                <div>
                  <div className="text-lg font-semibold text-gray-900">
                    {plan.maxTrips === -1 ? '∞' : plan.maxTrips}
                  </div>
                  <div className="text-xs text-gray-500">رحلة</div>
                </div>
              </div>
            </div>

            {/* Features */}
            {plan.features && plan.features.length > 0 && (
              <div className="p-6">
                <h4 className="font-medium text-gray-900 mb-3">المميزات</h4>
                <ul className="space-y-2">
                  {plan.features.map((feature, index) => (
                    <li key={index} className="flex items-center text-sm text-gray-600">
                      <span className="text-green-500 mr-2">✓</span>
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Status Badge */}
            <div className="p-6 pt-0">
              <span
                className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                  plan.isActive
                    ? 'bg-green-100 text-green-800'
                    : 'bg-red-100 text-red-800'
                }`}
              >
                {plan.isActive ? 'نشط' : 'غير نشط'}
              </span>
            </div>
          </div>
        ))}
      </div>

      {/* Create/Edit Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">
              {editingPlan ? 'تعديل خطة الاشتراك' : 'إضافة خطة جديدة'}
            </h2>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    اسم الخطة <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    السعر <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    value={formData.price}
                    onChange={(e) => setFormData(prev => ({ ...prev, price: Number(e.target.value) }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    min="0"
                    step="0.01"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    المدة <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    value={formData.duration}
                    onChange={(e) => setFormData(prev => ({ ...prev, duration: Number(e.target.value) }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    min="1"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    وحدة المدة <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={formData.durationUnit}
                    onChange={(e) => setFormData(prev => ({ ...prev, durationUnit: e.target.value as 'month' | 'year' }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    required
                  >
                    <option value="month">شهر</option>
                    <option value="year">سنة</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    الحد الأقصى للمستخدمين
                  </label>
                  <input
                    type="number"
                    value={formData.maxUsers}
                    onChange={(e) => setFormData(prev => ({ ...prev, maxUsers: Number(e.target.value) }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    min="-1"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    الحد الأقصى للحافلات
                  </label>
                  <input
                    type="number"
                    value={formData.maxBuses}
                    onChange={(e) => setFormData(prev => ({ ...prev, maxBuses: Number(e.target.value) }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    min="-1"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    الحد الأقصى للرحلات
                  </label>
                  <input
                    type="number"
                    value={formData.maxTrips}
                    onChange={(e) => setFormData(prev => ({ ...prev, maxTrips: Number(e.target.value) }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    min="-1"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  الوصف <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  rows={3}
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  المميزات
                </label>
                <div className="space-y-2">
                  {(formData.features || []).map((feature, index) => (
                    <div key={index} className="flex space-x-2 space-x-reverse">
                      <input
                        type="text"
                        value={feature}
                        onChange={(e) => handleFeatureChange(index, e.target.value)}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="أدخل ميزة"
                      />
                      <button
                        type="button"
                        onClick={() => removeFeature(index)}
                        className="px-3 py-2 text-red-600 hover:text-red-800"
                      >
                        حذف
                      </button>
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={addFeature}
                    className="text-blue-600 hover:text-blue-800 text-sm"
                  >
                    + إضافة ميزة
                  </button>
                </div>
              </div>

              <div className="flex justify-end space-x-3 space-x-reverse pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setIsModalOpen(false);
                    setEditingPlan(null);
                    resetForm();
                  }}
                  className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
                >
                  إلغاء
                </button>
                <button
                  type="submit"
                  disabled={createMutation.isPending || updateMutation.isPending}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-blue-400"
                >
                  {createMutation.isPending || updateMutation.isPending
                    ? 'جاري الحفظ...'
                    : editingPlan
                    ? 'تحديث الخطة'
                    : 'إنشاء الخطة'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default SubscriptionPlans;